import planner from "../content/cerimonialistas.js";
import type { PlannerContent, SplitCopy } from "../content/types.js";
import { WHATSAPP_NUMBER, whatsappTo } from "../shared/links.js";
import home, {
  type ControlCopy,
  type FlowCopy,
  type MenuCopy,
  type NumberCopy,
  type V2FootCopy,
  type WallCopy,
} from "./content.js";

export const DEMO_PATH = "/cerimonialistas/demo/";

export interface TeaserCopy {
  readonly label: string;
  readonly names: readonly string[];
  readonly chips: readonly string[];
}

export interface TourStep {
  readonly role: string;
  readonly title: string;
  readonly body: string;
}

export interface TourScreensCopy {
  readonly demo: string;
  readonly brandTitle: string;
  readonly nameLabel: string;
  readonly colorLabel: string;
  readonly logoLabel: string;
  readonly logoButton: string;
  readonly create: string;
  readonly live: string;
  readonly stats: string;
  readonly doors: readonly [string, string, string];
  readonly recent: string;
  readonly moderation: string;
  readonly hiddenLabel: string;
  readonly hide: string;
  readonly hidden: string;
  readonly all: string;
  readonly photos: string;
}

export interface TourCopy {
  readonly label: string;
  readonly title: string;
  readonly brand: string;
  readonly steps: readonly TourStep[];
  readonly screens: TourScreensCopy;
  readonly cta: string;
  readonly hint: string;
  readonly nudge: string;
  readonly nudgeLast: string;
}

export interface LaunchCopy {
  readonly label: string;
  readonly title: string;
  readonly body: string;
  readonly nameLabel: string;
  readonly placeholder: string;
  readonly colorLabel: string;
  readonly cta: string;
  readonly note: string;
  readonly qrTitle: string;
  readonly qrBody: string;
}

export interface PlannerV2Content extends PlannerContent {
  readonly menu: MenuCopy;
  readonly teaser: TeaserCopy;
  readonly numbers: readonly NumberCopy[];
  readonly tour: TourCopy;
  readonly launch: LaunchCopy;
  readonly control: ControlCopy;
  readonly angles: SplitCopy;
  readonly flow: FlowCopy;
  readonly wall: WallCopy;
  readonly ending: V2FootCopy;
}

const whatsapp = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(planner.meta.whatsapp)}`;

const content: PlannerV2Content = {
  ...planner,
  meta: { ...planner.meta, title: "Kuori para cerimonialistas" },
  menu: {
    product: "Produto",
    explore: "Explore a Kuori",
    items: [
      {
        href: "#demo",
        icon: "play",
        title: "A demonstração",
        body: "Sete passos, uns dois minutos, no seu celular.",
      },
      {
        href: "#teste",
        icon: "palette",
        title: "Com a sua marca",
        body: "Digite o nome do seu cerimonial e veja.",
      },
      {
        href: "#produto",
        icon: "monitor",
        title: "No dia",
        body: "O telão roda sozinho. Você só modera.",
      },
      {
        href: "#painel",
        icon: "grid",
        title: "Seu cerimonial",
        body: "Todos os seus clientes num painel.",
      },
    ],
    links: [
      {
        href: whatsappTo("Oi! Sou cerimonialista e quero um orçamento da Kuori."),
        icon: "whatsapp",
        title: "Pedir orçamento",
        external: true,
      },
      { href: "#faq", icon: "help", title: "Perguntas" },
    ],
    start: "Testar agora",
    startHref: DEMO_PATH,
    talk: "Fale com a Kuori",
  },
  hero: {
    ...planner.hero,
    sub: "Os convidados fotografam pelo QR, sem instalar nada, e as fotos vão para o álbum e para o telão ao vivo, com a marca do seu cerimonial. Veja funcionando em dois minutos, no seu celular.",
    cta: "Testar a demonstração",
    ctaHref: DEMO_PATH,
    alt: "Falar no WhatsApp",
    altHref: whatsapp,
  },
  teaser: {
    label: "Demonstração · com a sua marca",
    names: ["Ateliê Marília", "Studio Aurora", "Casa Lume"],
    chips: ["2 minutos", "Sem cadastro", "No seu celular"],
  },
  numbers: [
    { value: 2, unit: "min", label: "para montar um evento de exemplo com a sua marca" },
    { value: 0, label: "cadastros ou aplicativos para testar" },
    { value: 7, label: "passos guiados, do convite ao álbum que o seu cliente recebe" },
  ],
  tour: {
    label: "A demonstração",
    title: "Veja o evento dos três lados.",
    brand: "Ateliê Marília",
    steps: [
      {
        role: "Você, cerimonial",
        title: "Sua marca primeiro.",
        body: "Nome do cerimonial e cor. É assim que o seu cliente vê o evento.",
      },
      {
        role: "Você, cerimonial",
        title: "O evento pronto, ao vivo.",
        body: "O QR sai pronto para o convite, o cartão de mesa e o telão.",
      },
      {
        role: "Convidado",
        title: "Ele só digita o nome.",
        body: "Sem cadastro e sem aplicativo, até de dentro do WhatsApp.",
      },
      {
        role: "Convidado",
        title: "Dez fotos por convidado.",
        body: "Assim que ele envia, a foto vai para o álbum e para o telão.",
      },
      {
        role: "Telão",
        title: "O telão roda sozinho.",
        body: "Na TV ou no projetor, com o nome de quem tirou cada foto.",
      },
      {
        role: "Você, cerimonial",
        title: "Você decide o que aparece.",
        body: "Ocultou pelo celular, a foto sai do telão na hora.",
      },
      {
        role: "Seu cliente",
        title: "O álbum que o seu cliente recebe.",
        body: "Separado por horário, com a sua marca, pronto para entregar.",
      },
    ],
    screens: {
      demo: "Demonstração",
      brandTitle: "Monte um evento de exemplo com a sua marca",
      nameLabel: "Nome do cerimonial",
      colorLabel: "Cor de destaque",
      logoLabel: "Logotipo (opcional)",
      logoButton: "Escolher arquivo",
      create: "Criar evento",
      live: "ao vivo",
      stats: "17 fotos · 9 convidados",
      doors: ["Convite", "Telão", "Moderar"],
      recent: "Fotos recentes",
      moderation: "Moderação",
      hiddenLabel: "Ocultas",
      hide: "Ocultar",
      hidden: "Oculta",
      all: "Tudo",
      photos: "fotos",
    },
    cta: "Fazer a demonstração",
    hint: "Role para seguir",
    nudge: "Arraste para ver o próximo passo",
    nudgeLast: "Continue rolando",
  },
  launch: {
    label: "Sua vez",
    title: "Teste com o nome do seu cerimonial.",
    body: "Digite o nome e escolha a cor. O convite e o telão mudam aqui, e a demonstração já começa com a sua marca.",
    nameLabel: "Nome do seu cerimonial",
    placeholder: "Ateliê Marília",
    colorLabel: "Cor de destaque",
    cta: "Começar a demonstração",
    note: "Uns dois minutos. Tudo fica só no seu navegador.",
    qrTitle: "Prefere no celular?",
    qrBody: "Aponte a câmera para abrir a demonstração.",
  },
  control: {
    ...home.control,
    label: "No dia",
    title: "Nada para você operar.",
    body: "O telão roda sozinho numa TV ou num projetor. Revele as fotos na hora ou só no fim, e oculte uma foto pelo celular em segundos. Experimente aqui.",
  },
  angles: planner.photographer,
  flow: home.flow,
  wall: home.wall,
  ending: {
    title: planner.foot.cta,
    note: planner.foot.note,
    action: "Falar no WhatsApp",
    rights: planner.foot.rights,
    demo: { label: "Testar a demonstração", href: DEMO_PATH },
  },
};

export default content;
